/* eslint-disable react/prop-types */
import {
  Box,
  Card,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Stack,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { AccountTree as AccountTreeIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { ReportGmailerrorred as ReportGmailerrorredIcon } from "@mui/icons-material";

function WorkflowsList({
  workflowEntries,
  selectedWorkflowId,
  onWorkflowSelect,
  isLoading,
  hasError,
}) {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Card
      elevation={3}
      sx={{
        width: "35%",
        borderRadius: 3,
        overflow: "hidden",
        border: "1px solid",
        borderColor: "divider",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          p: 2.5,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
          borderBottom: "1px solid",
          borderColor: "divider",
          minHeight: 80,
          display: "flex",
          alignItems: "center",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ width: "100%" }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <AccountTreeIcon sx={{ color: "primary.main", fontSize: 28 }} />
            <Typography
              variant="h6"
              fontWeight="bold"
              color="text.primary"
              sx={{ lineHeight: 1.2 }}
            >
              {t("hazards.workflowsTitle")}
            </Typography>
          </Stack>
        </Stack>
      </Box>

      <Box sx={{ flex: 1, overflow: "auto" }}>
        {isLoading ? (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <CircularProgress size={48} sx={{ mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              {t("hazards.loadingHazards")}
            </Typography>
          </Box>
        ) : hasError ? (
          <Box sx={{ textAlign: "center", py: 8, px: 3 }}>
            <Box
              sx={{
                display: "inline-flex",
                p: 3,
                borderRadius: "50%",
                bgcolor: alpha(theme.palette.error.main, 0.1),
                mb: 2,
              }}
            >
              <ReportGmailerrorredIcon
                sx={{
                  fontSize: 40,
                  color: "error.main",
                  opacity: 0.7,
                }}
              />
            </Box>
            <Typography variant="body1" color="text.secondary" fontWeight="500">
              {t("hazards.errorTitle")}
            </Typography>
          </Box>
        ) : workflowEntries.length > 0 ? (
          <List sx={{ p: 2 }}>
            {workflowEntries.map(([workflowId, group]) => (
              <ListItemButton
                key={workflowId}
                selected={selectedWorkflowId === workflowId}
                onClick={() => onWorkflowSelect(workflowId)}
                sx={{
                  mb: 1,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor:
                    selectedWorkflowId === workflowId
                      ? "primary.main"
                      : "divider",
                  transition: "all 0.3s ease",
                  background:
                    selectedWorkflowId === workflowId
                      ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`
                      : "transparent",
                  "&:hover": {
                    borderColor: "primary.main",
                    boxShadow: theme.shadows[2],
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
                  },
                  "&.Mui-selected": {
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
                    "&:hover": {
                      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
                    },
                  },
                }}
              >
                <ListItemAvatar>
                  <Avatar
                    sx={{
                      bgcolor:
                        selectedWorkflowId === workflowId
                          ? alpha(theme.palette.primary.main, 0.2)
                          : alpha(theme.palette.primary.main, 0.1),
                      border: "2px solid",
                      borderColor:
                        selectedWorkflowId === workflowId
                          ? alpha(theme.palette.primary.main, 0.4)
                          : alpha(theme.palette.primary.main, 0.2),
                      width: 40,
                      height: 40,
                    }}
                  >
                    <AccountTreeIcon
                      sx={{
                        color: "primary.main",
                        fontSize: 20,
                      }}
                    />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Typography
                      variant="body1"
                      fontWeight={selectedWorkflowId === workflowId ? 700 : 500}
                      color="text.primary"
                    >
                      {group.workflow.name}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      {group.hazards.length} {t("hazards.hazardsInGroup")}
                    </Typography>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        ) : (
          <Box sx={{ textAlign: "center", py: 8, px: 3 }}>
            <Box
              sx={{
                display: "inline-flex",
                p: 3,
                borderRadius: "50%",
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                mb: 2,
              }}
            >
              <AccountTreeIcon
                sx={{
                  fontSize: 40,
                  color: "primary.main",
                  opacity: 0.7,
                }}
              />
            </Box>
            <Typography variant="body1" color="text.secondary" fontWeight="500">
              {t("hazards.noWorkflows")}
            </Typography>
          </Box>
        )}
      </Box>
    </Card>
  );
}

export default WorkflowsList;
